import React, { useState, useEffect } from 'react';
import { Modal } from '../../../components/Modal';
import { dmsService } from '../../../services/dmsService';

interface ReplaceVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  document: any | null;
}

export const ReplaceVersionModal: React.FC<ReplaceVersionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  document,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setError(null);
    }
  }, [isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!document) return;
    if (!selectedFile) {
      setError('Please select a replacement file.');
      return;
    }

    setIsLoading(true);
    setError(null);

    const submitData = new FormData();
    submitData.append('file', selectedFile);

    try {
      await dmsService.replaceDocumentVersion(document.id, submitData);
      onSave(); // Refresh main list
    } catch (err: any) {
      console.error('Failed to replace document version:', err);
      setError(err.message || 'Failed to replace document version.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Replace Document File">
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && (
            <div
              style={{
                color: '#dc2626',
                backgroundColor: '#fee2e2',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          {document && (
            <div
              style={{
                backgroundColor: 'rgba(71, 49, 146, 0.05)',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                fontSize: '0.95rem',
              }}
            >
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Current Active File: </strong>
                {document.current_version_detail?.original_name || 'No file uploaded'}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Current Version: </strong>
                v{document.current_version_detail?.version_number || 0}
              </div>
              <div>
                <strong>Confidentiality: </strong>
                {document.confidentiality}
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="filter-group form-full">
              <label>Select New File Version *</label>
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,image/jpeg,image/png"
                required
              />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                Supported formats: PDF, Office, Text, Images (Max 50MB)
              </span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="secondary-btn"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button type="submit" className="primary-btn" disabled={isLoading}>
            {isLoading ? 'Uploading...' : 'Upload New Version'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
